import * as React from 'react';
import initControls from './controls/init';
import { loadHls } from './loadHls';
import VideoDetails from 'models/VideoDetails';
import { DRMLicenseType } from 'enums/drm';
import PlatformHelper from 'helpers/PlatformHelper';
import { getSubsLanguagePreference, saveSubsLanguagePreference, toggleFullScreen } from 'helpers/player';
import { loadDash } from './loadDash';
import Head from 'next/head';
import { useLang } from 'context/langContext';
import useTranslation from 'next-translate/useTranslation';
import { Namespace } from 'enums';
const shaka = require('external/shaka-player.ui.debug');
shaka.polyfill.installAll();

let player: any;
let noNetworkTimeout: number;
let noNetworkErrorTimeout: number;
let endCreditsRollingEventSent = false;
let keyDownEvent: (e: Event) => void;

const NO_NETWORK_LOADER_TOGGLE_TIMEOUT = 7000;
const NO_NETWORK_ERROR_TIMEOUT = 30000;

interface Props {
  videoDetails: VideoDetails;
  onTimeoutError: () => void;
  onError: () => void;
  onPlay: () => void;
  onPlaybackEnded: () => void;
  onSubtitlesChanged?: (langCode: string) => void;
  onEndCreditsStarted: () => void;
  onClose: () => void;
  onPause: () => void;
  onInit: (video: HTMLVideoElement) => void;
  // returns new DRM info
  onNewDRMCustomDataRequest: () => Promise<string>;
}

const Video = ({
  videoDetails,
  onTimeoutError,
  onError,
  onPlay,
  onPlaybackEnded,
  onSubtitlesChanged,
  onEndCreditsStarted,
  onNewDRMCustomDataRequest,
  onClose,
  onPause,
  onInit,
}: Props): JSX.Element => {
  const [displayNativeSubtitleTracks] = React.useState(false);
  const [nativeSubtitleTracks, setNativeSubtitleTracks] = React.useState<JSX.Element[]>([]);
  const [loading, setLoading] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const videoContainerRef = React.useRef<HTMLDivElement>(null);

  const { locale } = useLang();
  const { t } = useTranslation(Namespace.Player);

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    // window._s = videoDetails.subtitles;

    window.requestDRMCustomData = onNewDRMCustomDataRequest;

    if (videoRef.current) {
      document.body.classList.add('overflow-hidden');

      const video = videoRef.current;
      const container = videoContainerRef.current;

      // init player
      player = new shaka.Player(video);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      window._p = player;

      // init controls
      const controls = initControls(player, video, container, locale, t) as ShakaControl;

      // attach custom stuff
      controls.addEventListener('titlesComponentInitialized', (event) => {
        // fill in title
        const titlesControl = event.component;
        titlesControl.setPrimaryTitle(videoDetails.title);
        titlesControl.setSecondaryTitle(videoDetails.secondaryTitle);
      });

      controls.addEventListener('closeButtonInitialized', (event) => {
        // attach onClose click event
        const closeButtonControl = event.component;
        closeButtonControl.setOnClick(_onClose);
      });

      controls.getCastProxy().videoEventTarget_.addEventListener('ended', function () {
        controls.getCastProxy().isCasting() && _onClose();
      });

      /*
       * Shaka player doesn't support external subtitles in Safari when playing m3u8, it has to be part of the manifest.
       * We need to insert subtitles as a common HTML5 <track>.
       * Still, the subs are not displayed via shaka.text.UITextDisplayer but they use a common video::cue styling.
       */
      const subtitles = videoDetails.subtitles?.map((s) => (
        <track label={s.label} kind="subtitles" srcLang={s.language} src={s.url} key={s.language}></track>
      ));
      setNativeSubtitleTracks(subtitles);

      // callback when player is loaded
      // add subtitles here (non-Apple devices) and perform other after load operations
      const onPlayerLoaded = () => {
        console.log('Player initialized.');
        if (!PlatformHelper.isApple) {
          player.manifest_.textStreams = [];
          videoDetails.subtitles?.forEach((subs) => {
            player.addTextTrack(subs.url, subs.language, 'subtitle', 'text/vtt', '', subs.label);
          });
        }
        // pass video reference above

        onInit(video);
      };

      // load video + DRM based on browser-specific license/certificate
      const licenseType = PlatformHelper.drmLicenseType;
      if (licenseType === DRMLicenseType.FairPlay) {
        // Safari + iOS
        loadHls(videoDetails, player).then(onPlayerLoaded);
      } else {
        // Chrome + FF + IE + Android
        loadDash(videoDetails, player).then(onPlayerLoaded);
      }

      // EVENTS
      const _onClose = () => {
        killChromeCast();
        onClose();
      };

      const killChromeCast = () => {
        controls.getCastProxy().isCasting() && controls.getCastProxy().forceDisconnect();
      };

      const onNetworkFound = () => {
        window.clearTimeout(noNetworkTimeout);
        window.clearTimeout(noNetworkErrorTimeout);

        setLoading(false);
        video.play();
      };

      const onNetworkLost = (loaderTimeout = NO_NETWORK_LOADER_TOGGLE_TIMEOUT) => {
        window.clearTimeout(noNetworkTimeout);
        window.clearTimeout(noNetworkErrorTimeout);

        noNetworkTimeout = window.setTimeout(() => {
          video.pause();
          setLoading(true);
        }, loaderTimeout);

        noNetworkErrorTimeout = window.setTimeout(() => {
          killChromeCast();
          onTimeoutError();
        }, NO_NETWORK_ERROR_TIMEOUT);
      };

      const _onSubtitlesChanged = (event?: CustomEvent) => {
        if (event) {
          onSubtitlesChanged(event.detail);
        }
        saveSubsLanguagePreference(event?.detail);
      };

      const _onSubtitlesVisibilityChanged = () => {
        if (!player.isTextTrackVisible()) {
          _onSubtitlesChanged();
        }
      };

      player.addEventListener('error', function (event) {
        console.log('Error', event.detail);
        onError();
      });

      video.addEventListener('loadeddata', function () {
        if (videoDetails.watchProgress) {
          setTimeout(() => {
            video.currentTime = videoDetails.watchDuration;
          });
        }

        if (PlatformHelper.isApple) {
          const subs = player.getTextTracks().filter((t) => t.language !== '');
          if (subs.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { ui } = videoRef.current as HTMLVideoElement & { ui: any };
            const uiConfig = ui.getConfiguration();
            uiConfig.overflowMenuButtons = uiConfig.overflowMenuButtons.filter((b) => b !== 'captions');
            ui.configure(uiConfig);
          }
        }

        // load & show preffered subtitles (if any)
        setTimeout(() => {
          const prefferedSubsLang = getSubsLanguagePreference();
          if (prefferedSubsLang) {
            const track = (player.getTextTracks().filter((t) => t.language === prefferedSubsLang) ?? [])[0];
            if (track) {
              player.selectTextTrack(track);
              player.setTextTrackVisibility(true);
            }
          }
        }, 50);

        setLoading(false);
      });

      video.addEventListener('ended', function () {
        killChromeCast();
        onPlaybackEnded();
      });

      if (videoDetails.endCreditsTime) {
        video.addEventListener('timeupdate', function () {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (!video.ui.isMobile() && video.currentTime > videoDetails.endCreditsTime && !endCreditsRollingEventSent) {
            endCreditsRollingEventSent = true;
            onEndCreditsStarted();
          }
        });
      }

      window.addEventListener('captionschanged', _onSubtitlesChanged, true);
      player.addEventListener('texttrackvisibility', _onSubtitlesVisibilityChanged);

      player.addEventListener('buffering', function (event) {
        switch (event.buffering) {
          case true:
            onNetworkLost();
            break;
          default:
            onNetworkFound();
        }
      });

      video.addEventListener('playing', function () {
        onNetworkFound();
      });

      videoRef.current.addEventListener('play', function () {
        if (videoRef.current) {
          onPlay();
        }
      });

      video.addEventListener('pause', function () {
        onPause();
      });

      player.addEventListener('onstatechange', function () {
        // console.log(event);
      });

      window.addEventListener('beforeunload', function () {
        killChromeCast();
      });

      window.addEventListener('orientationchange', function () {
        setTimeout(() => {
          window.scroll(0, 1);
          video.focus();
        }, 500);
      });

      keyDownEvent = function (event: KeyboardEvent) {
        event.stopImmediatePropagation();

        // blur active element so we don't accidentally press some focused button
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        document.activeElement?.blur();

        const { duration } = video;
        let seekTo: number;

        // perform action on next frame
        setTimeout(() => {
          switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowRight':
              const direction = event.key === 'ArrowRight' ? 1 : -1;
              seekTo = video.currentTime + 10 * direction;
              // leaving this here if we want to display floating bubble on right/left press in the future

              // const progress = Math.min(Math.max(shift / video.duration, 0), 1);
              // const seekBarElement = controls.seekBar_.container as HTMLElement;

              // console.log(seekBarElement.offsetLeft, seekBarElement.clientWidth, progress);

              // controls.dispatchEvent(
              //   new shaka.util.FakeEvent('seekBarMouseOver', {
              //     progress,
              //     screenX: seekBarElement.offsetLeft + 10 + seekBarElement.clientWidth * progress,
              //     seekBarHorizontalMargin: seekBarElement.offsetLeft,
              //   }),
              // );
              break;
            case 'Home':
              controls.seek_(player.seekRange().start, event);
              break;
            case 'End':
              controls.seek_(player.seekRange().end, event);
              break;
            case ' ':
              controls.onPlayPauseClick_();
              break;
            case 'f':
              toggleFullScreen(video);
              break;
            case 'Escape':
              _onClose();
              break;
            case '1':
            case '+':
              seekTo = duration * 0.1;
              break;
            case '2':
            case 'ě':
              seekTo = duration * 0.2;
              break;
            case '3':
            case 'š':
              seekTo = duration * 0.3;
              break;
            case '4':
            case 'č':
              seekTo = duration * 0.4;
              break;
            case '5':
            case 'ř':
              seekTo = duration * 0.5;
              break;
            case '6':
            case 'ž':
              seekTo = duration * 0.6;
              break;
            case '7':
            case 'ý':
              seekTo = duration * 0.7;
              break;
            case '8':
            case 'á':
              seekTo = duration * 0.8;
              break;
            case '9':
            case 'í':
              seekTo = duration * 0.9;
              break;
            case '0':
            case 'é':
              seekTo = 0.1;
          }

          if (seekTo) {
            controls.seek_(seekTo, event);
            controls.onMouseMove_(event);
          }
        });
      };

      window.addEventListener('keydown', keyDownEvent, false);

      // add custom class when going iOS fullscreen
      video.addEventListener('webkitpresentationmodechanged', () => {
        const CLS = 'ios-fullscreen';
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (video.webkitPresentationMode === 'fullscreen') {
          document.body.classList.add(CLS);
        } else {
          document.body.classList.remove(CLS);
        }
      });
    }
  }, [videoRef]);

  React.useEffect(() => {
    return () => {
      document.body.classList.remove('overflow-hidden');
      window.removeEventListener('keydown', keyDownEvent, false);
      endCreditsRollingEventSent = false;

      player.unload();
      player.destroy();
    };
  }, []);

  return (
    <>
      {window && !(window as { cast?: unknown }).cast && (
        <Head>
          <script src="https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1" />
        </Head>
      )}
      <div className="video-container" data-shaka-player-container ref={videoContainerRef}>
        {loading && <div className="loader" />}
        <video
          data-shaka-player
          ref={videoRef}
          playsInline
          autoPlay
          x-webkit-airplay="allow"
          crossOrigin="anonymous"
          id="video"
        >
          {displayNativeSubtitleTracks && nativeSubtitleTracks}
        </video>
      </div>
    </>
  );
};

export default Video;
